<template>
  <div class="genre-category-setting-mask" @click.self="onClickCancel">
    <div
      ref="dialogRoot"
      class="genre-category-setting-dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="genre-category-setting-dialog__header">
        <div class="genre-category-setting-dialog__header-spacer" aria-hidden="true" />
        <h2 :id="titleId" class="genre-category-setting-dialog__title">
          {{ $t("novelDetail.genreCategorySetting.title") }}
        </h2>
        <button
          type="button"
          class="genre-category-setting-dialog__close"
          :aria-label="String($t('common.close'))"
          @click="onClickCancel"
        >
          <IconClose />
        </button>
      </header>

      <div class="genre-category-setting-section">
        <h3 :id="categorySelectLabelId" class="genre-category-setting-label">
          {{ $t("novelDetail.categoryLabel") }}
        </h3>
        <div class="genre-category-setting-select-wrap">
          <select
            class="genre-category-setting-select"
            :value="genreId ?? ''"
            :aria-labelledby="categorySelectLabelId"
            @change="onChangeGenre"
          >
            <option value="">{{ $t("novelDetail.genreCategorySetting.categoryPlaceholder") }}</option>
            <option v-for="item in translatedGenreList" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
          <IconChevronDown class="genre-category-setting-select-icon" aria-hidden="true" />
        </div>
      </div>

      <div class="genre-category-setting-section">
        <h3 class="genre-category-setting-label">
          {{ $t("novelDetail.genreLabel")
          }}{{ $t("novelDetail.genreCategorySetting.genreHint", { count: MAX_CATEGORY_COUNT }) }}
        </h3>
        <div class="genre-category-setting-pills">
          <button
            v-for="item in translatedCategoryList"
            :key="item.id"
            type="button"
            class="genre-category-setting-pill"
            :class="{ selected: categoryIds.includes(item.id) }"
            :aria-pressed="categoryIds.includes(item.id)"
            :disabled="!categoryIds.includes(item.id) && categoryIds.length >= MAX_CATEGORY_COUNT"
            @click="onToggleCategory(item.id)"
          >
            {{ item.name }}
          </button>
        </div>
      </div>

      <button
        type="button"
        class="genre-category-setting-update-button"
        :disabled="isSaving || !canSubmit"
        @click="onClickUpdate"
      >
        {{ $t("novelDetail.genreCategorySetting.updateButton") }}
      </button>
      <button type="button" class="genre-category-setting-cancel-button" :disabled="isSaving" @click="onClickCancel">
        {{ $t("common.cancel") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef -- defineProps is a Vue compiler macro */
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
  toggleCategorySelection,
  canSubmitGenreCategory,
  MAX_CATEGORY_COUNT,
} from "@/lib/utils/novelSettingsHelper";
import { Novel } from "@/lib/models";
import IconClose from "~icons/figma/Close";
import IconChevronDown from "~icons/figma/ChevronDown";

const props = defineProps<{
  novelId: string;
}>();

const titleId = "genre-category-setting-title";
const categorySelectLabelId = "genre-category-setting-category-select-label";

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const store = proxy?.$store;

const translatedGenreList = proxy ? getTranslatedGenreList(proxy.$t.bind(proxy)) : [];
const translatedCategoryList = proxy ? getTranslatedCategoryList(proxy.$t.bind(proxy)) : [];

const novel = store?.getters["novelModule/novel"](props.novelId) as Novel | undefined;

// 廃止・不正な選択肢IDを持つレガシーデータの場合、選択肢一覧に存在しないIDをそのまま保持すると
// セレクトが空欄のままcanSubmitがtrueになったり、見えないIDが上限枠を消費してしまうため、
// 初期化時に選択肢一覧に存在するIDのみを残す
const validGenreIds = new Set(translatedGenreList.map((item) => item.id));
const validCategoryIds = new Set(translatedCategoryList.map((item) => item.id));

const genreId = ref<string | undefined>(novel?.genre && validGenreIds.has(novel.genre) ? novel.genre : undefined);
const categoryIds = ref<string[]>((novel?.category ?? []).filter((id) => validCategoryIds.has(id)));
const isSaving = ref(false);
const dialogRoot = ref<HTMLElement | null>(null);

// ジャンル・カテゴリはともに必須（未選択のまま保存できてしまうと、作品作成時のバリデーションと
// 矛盾するデータ状態を作ってしまうため）
const canSubmit = computed(() => canSubmitGenreCategory(genreId.value, categoryIds.value));

function onClickCancel() {
  if (isSaving.value) return;
  proxy?.$close(false);
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(): HTMLElement[] {
  if (!dialogRoot.value) return [];
  return Array.from(dialogRoot.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// Escapeで閉じ、Tabがダイアログの外(背後のページ)へ抜けないようループさせる
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    onClickCancel();
    return;
  }
  if (e.key !== "Tab") return;

  const focusable = getFocusableElements();
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  // 開いた直後はfirst/lastどちらでもないダイアログ本体(tabindex="-1")にフォーカスがあるため、
  // activeElementがfirst/lastの場合だけでなく、ダイアログ本体そのものの場合も起点として扱う
  // (これを入れないと、開いた直後の1回目のShift+Tabだけダイアログの外(背後のページ)へ抜けてしまう)
  const atStart = document.activeElement === first || document.activeElement === dialogRoot.value;
  const atEnd = document.activeElement === last || document.activeElement === dialogRoot.value;

  if (e.shiftKey && atStart) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && atEnd) {
    e.preventDefault();
    first.focus();
  }
}

onMounted(() => {
  nextTick(() => dialogRoot.value?.focus());
  document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));

function onChangeGenre(event: Event) {
  const target = event.target as HTMLSelectElement;
  genreId.value = target.value || undefined;
}

function onToggleCategory(id: string) {
  categoryIds.value = toggleCategorySelection(categoryIds.value, id, MAX_CATEGORY_COUNT);
}

async function onClickUpdate() {
  if (!canSubmit.value) {
    return;
  }

  isSaving.value = true;
  try {
    if (!store?.getters["novelModule/novel"](props.novelId)) {
      // 作品がストアから消えている場合は何度リトライしても保存できないため、
      // 通知したうえでダイアログを閉じる（開いたままだとクリックの度にトーストが増え続ける）
      proxy?.$notify({
        title: proxy?.$t("novelDetail.genreCategorySetting.saveFailed"),
        type: "error",
      });
      proxy?.$close(false);
      return;
    }

    const updated = await store?.dispatch("novelModule/updateNovel", {
      novelId: props.novelId,
      genre: genreId.value,
      category: categoryIds.value,
    });
    if (!updated) {
      // GraphQLがエラーを含む場合等、実際には保存されていないため成功扱いにしない
      // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
      return;
    }

    proxy?.$notify({
      title: proxy?.$t("common.saved"),
      type: "success",
      duration: 2000,
    });
    proxy?.$close(true);
  } catch (error) {
    // 通信エラー等は updateNovelの呼び出し元であるGraphQLClient側で
    // 既にエラーダイアログの表示・ログ送信が行われているため、ここでは重複させない
  } finally {
    isSaving.value = false;
  }
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.genre-category-setting-mask {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.genre-category-setting-dialog {
  width: 100%;
  max-width: 400px;
  margin: 0 16px;
  padding: 20px 20px 24px;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.genre-category-setting-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.genre-category-setting-dialog__header-spacer {
  width: 28px;
  flex-shrink: 0;
}

.genre-category-setting-dialog__title {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
}

.genre-category-setting-dialog__close {
  position: relative;
  @include hover-overlay;
  display: flex;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-normal);
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }
}

.genre-category-setting-section {
  margin-bottom: 20px;
}

.genre-category-setting-label {
  margin: 0 0 8px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.genre-category-setting-select-wrap {
  position: relative;
}

.genre-category-setting-select {
  width: 100%;
  padding: 10px 36px 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  appearance: none;
  cursor: pointer;
}

.genre-category-setting-select-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: var(--color-text-low);
  pointer-events: none;
}

.genre-category-setting-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.genre-category-setting-pill {
  position: relative;
  padding: 8px 14px;
  border: none;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  cursor: pointer;
  @include hover-overlay;

  &.selected {
    background-color: var(--color-background-invert);
    color: var(--color-text-invert);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &::after {
      display: none;
    }
  }
}

.genre-category-setting-update-button {
  position: relative;
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  @include hover-overlay;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.genre-category-setting-cancel-button {
  position: relative;
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  @include hover-overlay;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
